import React, { useState, useEffect, useCallback } from 'react';
import MovieCard, { ShimmerCard } from './MovieCard';
import { useToast } from './Toast';
import api from '../services/api';

const TMDB_IMG = 'https://image.tmdb.org/t/p/w342';

function HeroPosterCollage({ posters, loading }) {
  return (
    <div className="hero-posters" aria-hidden="true">
      {loading || posters.length === 0 ? (
        [0, 1, 2, 3].map(i => (
          <div key={i} className={`hero-poster hero-poster-${i + 1} shimmer-card`} />
        ))
      ) : (
        posters.map((movie, i) => (
          <div key={movie.id} className={`hero-poster hero-poster-${i + 1}`}>
            <img
              src={`${TMDB_IMG}${movie.poster_path}`}
              alt=""
              draggable={false}
            />
          </div>
        ))
      )}
    </div>
  );
}

// A movie is "now showing" if it was released within the last 7 days.
function computeIsNowShowing(movie) {
  if (!movie.release_date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const rel = new Date(movie.release_date + 'T00:00:00');
  return rel <= today && rel >= sevenDaysAgo;
}

// Retry an async fn up to `retries` times with `delay` ms between attempts.
async function fetchWithRetry(fn, retries = 3, delay = 1000) {
  let lastErr;
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (i < retries - 1) await new Promise(r => setTimeout(r, delay));
    }
  }
  throw lastErr;
}

export default function Browse({ user, watchlistIds, onWatchlistChange, onShowAuth }) {
  const [movies,      setMovies]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [fetchError,  setFetchError]  = useState(false);
  const [heroPosters, setHeroPosters] = useState([]);
  const [heroLoading, setHeroLoading] = useState(true);
  const { toast } = useToast();

  // Hero collage — independent fetch, also retries silently
  useEffect(() => {
    fetchWithRetry(() => api.getMovies('coming_soon'))
      .then(data => {
        setHeroPosters(
          (data.movies || []).filter(m => m.poster_path).slice(0, 4)
        );
      })
      .catch(() => {})
      .finally(() => setHeroLoading(false));
  }, []);

  const fetchMovies = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const data = await fetchWithRetry(() => api.getMovies('all'));
      setMovies(data.movies || []);
    } catch {
      setFetchError(true);
      toast('Failed to load movies. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMovies(); }, [fetchMovies]);

  async function handleWatchlistToggle(movieId, isIn) {
    if (!user) { onShowAuth('signup'); return; }
    const movie = movies.find(m => m.id === movieId);
    const title = movie?.title;
    try {
      if (isIn) {
        await api.removeFromWatchlist(movieId);
        toast('Alert removed from your watchlist.', 'info');
        onWatchlistChange(movieId, false);
      } else {
        await api.addToWatchlist(movieId);
        toast(
          title
            ? `You're all set! We'll alert you when tickets go on sale for ${title}.`
            : "You're all set! We'll alert you when tickets go on sale.",
          'success'
        );
        onWatchlistChange(movieId, true);
      }
    } catch (err) {
      if (/already in watchlist/i.test(err.message)) {
        toast("Already on your list! We'll notify you when tickets drop.", 'info');
      } else {
        toast(err.message, 'error');
      }
    }
  }

  return (
    <div>
      {/* ── Hero (non-logged-in only) ── */}
      {!user && (
        <div className="hero">
          <div className="hero-inner">
            <div className="hero-copy">
              <div className="hero-eyebrow">
                <span>🔔</span> Utah Theaters · Megaplex &amp; Cinemark
              </div>
              <h1 className="hero-title">
                Never miss a<br />
                <em>ticket drop.</em>
              </h1>
              <p className="hero-sub">
                Get instant SMS and email alerts the moment tickets go on sale
                for movies you want to see at Utah&apos;s Megaplex and Cinemark theaters.
              </p>
              <div className="hero-actions">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => onShowAuth('signup')}
                >
                  Get Started Free
                </button>
                <button
                  className="btn btn-ghost btn-lg"
                  onClick={() => onShowAuth('login')}
                >
                  Sign In
                </button>
              </div>
              <p className="hero-note">
                <span className="hero-note-check">✓</span> Free
                <span className="hero-note-check">✓</span> No credit card required
                <span className="hero-note-check">✓</span> Cancel anytime
              </p>
            </div>

            <HeroPosterCollage posters={heroPosters} loading={heroLoading} />
          </div>
        </div>
      )}

      {/* ── Sign-up nudge (logged out) ── */}
      {!user && !loading && movies.length > 0 && (
        <div className="signup-banner">
          <div className="signup-banner-inner" style={{ marginTop: 20 }}>
            <span className="signup-banner-text">
              <strong>Get notified instantly</strong> — sign up free and we&apos;ll text &amp; email
              you the moment tickets drop for any movie on your list.
            </span>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => onShowAuth('signup')}
            >
              Sign Up Free
            </button>
          </div>
        </div>
      )}

      {/* ── Section label ── */}
      {!loading && movies.length > 0 && (
        <div className="section-bar">
          <h3>Movies</h3>
          <span className="count">{movies.length} movies</span>
        </div>
      )}

      {/* ── Grid ── */}
      {loading ? (
        <div className="movie-grid-loading">
          {Array.from({ length: 12 }).map((_, i) => <ShimmerCard key={i} />)}
        </div>
      ) : fetchError ? (
        <div className="empty-state">
          <div className="empty-icon">⚠️</div>
          <h3>Couldn't load movies</h3>
          <p>Something went wrong. Please try again.</p>
          <button className="btn btn-primary" onClick={fetchMovies} style={{ marginTop: 16 }}>
            Retry
          </button>
        </div>
      ) : movies.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎬</div>
          <h3>No movies found</h3>
          <p>The catalog updates every 30 minutes. Check back shortly.</p>
        </div>
      ) : (
        <div className="movie-grid">
          {movies.map(movie => (
            <MovieCard
              key={movie.id}
              movie={movie}
              inWatchlist={watchlistIds.has(movie.id)}
              onWatchlistToggle={handleWatchlistToggle}
              isLoggedIn={!!user}
              isNowShowing={computeIsNowShowing(movie)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
